import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import GuestService from "@/app/_services/GuestService";
import { toast } from "@/app/_utils/toast";

export const intakeList = [
  { value: 10, label: "Shopify" },
  { value: 11, label: "Added at show" },
  { value: 12, label: "Eventbrite" },
  { value: 14, label: "Waitlist" },
  { value: 15, label: "Gallery" },
  { value: 16, label: "Stripe" },
];

export type WaitlistFilters = {
  num_of_shows: number;
  intake: number[];
  signup: { start: string; end: string };
  position: { start: number; end: number };
  showType: string;
};

export type SortModel = { field: string; sort: "asc" | "desc" }[];

const PAGE_SIZE = 1000;

export function useWaitlistModel() {
  const [rows, setRows] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [search, setSearch] = useState("");
  const [sortModel, setSortModel] = useState<SortModel | undefined>();
  const [filters, setFilters] = useState<WaitlistFilters | undefined>();
  const [filterDefaults, setFilterDefaults] = useState<WaitlistFilters | undefined>();

  const [page, setPage] = useState(0);

  const [editingGuest, setEditingGuest] = useState<any | null>(null);
  const [deletingGuest, setDeletingGuest] = useState<any | null>(null);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);

  async function loadFirstPage(overrideFilters?: WaitlistFilters) {
    setIsLoading(true);
    try {
      const res = await GuestService.getGuests({
        search,
        paginationModel: { pageSize: PAGE_SIZE, page: 0 },
        sortModel,
        filters: overrideFilters ?? filters,
      });
      const { count, data, guest_count } = res;
      if (!filters && !overrideFilters) {
        const defaults: WaitlistFilters = {
          num_of_shows: 0,
          intake: intakeList.map((i) => i.value),
          signup: {
            start: dayjs("01/01/2020").format("YYYY-MM-DD"),
            end: dayjs().add(1, "days").format("YYYY-MM-DD"),
          },
          position: { start: 1, end: guest_count + 1000 },
          showType: "all",
        };
        setFilterDefaults(defaults);
        setFilters(defaults);
      }
      setCount(count);
      setRows(data);
      setPage(0);
    } catch {
      toast.error("Failed to load waitlist");
    } finally {
      setIsLoading(false);
    }
  }

  async function loadMore() {
    setIsLoadingMore(true);
    try {
      const nextPage = page + 1;
      const res = await GuestService.getGuests({
        search,
        paginationModel: { pageSize: PAGE_SIZE, page: nextPage },
        sortModel,
        filters,
      });
      const { count, data } = res;
      setCount(count);
      setRows((prev) => prev.concat(data.filter((d: any) => !prev.some((r) => r.id === d.id))));
      setPage(nextPage);
    } catch {
      toast.error("Failed to load more guests");
    } finally {
      setIsLoadingMore(false);
    }
  }

  function applyFilters(next: WaitlistFilters) {
    setFilters(next);
    loadFirstPage(next);
  }

  function resetFilters() {
    if (filterDefaults) {
      setFilters(filterDefaults);
    }
  }

  async function handleDelete() {
    if (!deletingGuest) return;
    try {
      const res = await GuestService.deletGuest(deletingGuest.id);
      if (!res.ok) throw new Error();
      toast.success("Guest deleted");
      setDeletingGuest(null);
      loadFirstPage();
    } catch {
      toast.error("Failed to delete guest");
    }
  }

  function handleSort(next: SortModel) {
    setSortModel(next);
  }

  useEffect(() => {
    loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortModel]);

  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      loadFirstPage();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return {
    rows,
    count,
    isLoading,
    isLoadingMore,
    hasMore: rows.length < count,
    search,
    setSearch,
    filters,
    applyFilters,
    resetFilters,
    loadMore,
    editingGuest,
    setEditingGuest,
    deletingGuest,
    setDeletingGuest,
    csvUploadOpen,
    setCsvUploadOpen,
    handleDelete,
    handleSort,
    sortModel,
    refetch: () => loadFirstPage(),
  };
}
